import Title from "@/components/common/Title";
import FooterButtons from "@/components/common/FooterButtons";
import { studyByIdQueryOptions, studiesQueryKey } from "@/features/studies/api/selectStudies";
import { upsertStudy } from "@/features/studies/api/upsertStudy";
import { StudyForm, studyToFormValues, type StudyFormValues } from "@/features/studies/components/StudyForm";
import { studyTrackingQueryKey } from "@/features/studies/api/selectStudyTracking";
import {
    fetchStudyAccessById,
    saveStudyAccessPreset,
    studyAccessQueryKey,
} from "@/features/clinical-access/api/selectStudyAccess";
import { emptyStudyAccess } from "@/features/studies/utils/inferStudyAccessFromTemplates";
import { useStudiesCopy } from "@/features/i18n/studiesCopy";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "@patient-watch/ui/sonner";

function StudyEditorPage() {
    const { studyId } = Route.useParams();
    const copy = useStudiesCopy();
    const auth = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isAdmin = auth.roles?.isAdmin || auth.isSuperUser;
    const previousAccessRef = useRef(emptyStudyAccess());

    const { data: existingStudy, isLoading: studyLoading } = useQuery({
        ...studyByIdQueryOptions(studyId),
        retry: false,
    });

    const { data: loadedAccess, isLoading: accessLoading } = useQuery({
        queryKey: [...studyAccessQueryKey, "detail", studyId],
        queryFn: () => fetchStudyAccessById(studyId),
        enabled: Boolean(existingStudy),
        retry: false,
    });

    const isNew = !existingStudy && !studyLoading;
    const pageTitle = isNew ? copy.newStudyTitle : copy.editStudyTitle;
    const pageDescription = isNew ? copy.newStudyDescription : copy.editStudyDescription;

    useNav([
        { route: { to: "/studies" }, label: copy.studies },
        {
            route: { to: "/studies/$studyId", params: { studyId } },
            label: existingStudy?.study_name ?? copy.studies,
        },
        { route: { to: "/studies/$studyId/edit", params: { studyId } }, label: pageTitle },
    ]);

    const [values, setValues] = useState<StudyFormValues>(() => studyToFormValues(existingStudy));

    useEffect(() => {
        if (!existingStudy && !studyLoading) {
            const empty = studyToFormValues(null, emptyStudyAccess());
            setValues(empty);
            previousAccessRef.current = empty.access;
            return;
        }
        if (existingStudy && loadedAccess) {
            const next = studyToFormValues(existingStudy, loadedAccess);
            setValues(next);
            previousAccessRef.current = loadedAccess;
        }
    }, [existingStudy, loadedAccess, studyLoading]);

    const saveMutation = useMutation({
        mutationFn: async (next: StudyFormValues) => {
            await upsertStudy({
                studyId,
                studyName: next.studyName,
                billingPlan: next.billingPlan || null,
                expiryDate: next.expiryDate || null,
                isPublished: next.isPublished,
                organisationId: next.organisationId || null,
                specialty: next.specialty || null,
                labelIds: next.access.labels.private_access,
                formIds: next.access.forms.private_access,
                templateIds: next.access.templateIds,
            });

            const previousAccess = previousAccessRef.current;
            const previousAfterUpsert = {
                ...previousAccess,
                forms: { ...previousAccess.forms, private_access: [...next.access.forms.private_access] },
                labels: { ...previousAccess.labels, private_access: [...next.access.labels.private_access] },
                templateIds: [...next.access.templateIds],
            };

            await saveStudyAccessPreset({
                previous: {
                    id: studyId,
                    name: next.studyName,
                    studyName: next.studyName,
                    description: "",
                    access: previousAfterUpsert,
                },
                next: {
                    id: studyId,
                    name: next.studyName,
                    studyName: next.studyName,
                    description: "",
                    access: next.access,
                },
                updatedBy: auth.id,
            });
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: [studiesQueryKey] }),
                queryClient.invalidateQueries({ queryKey: studyAccessQueryKey }),
                queryClient.invalidateQueries({ queryKey: studyTrackingQueryKey(studyId) }),
            ]);
            toast.success(copy.studySaved);
            void navigate({ to: "/studies/$studyId", params: { studyId } });
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : copy.failedToSaveStudy);
        },
    });

    const handleSave = () => {
        saveMutation.mutate(values);
    };

    if (!isNew && (studyLoading || accessLoading)) {
        return (
            <section className="space-y-8 mx-auto w-full min-w-0 max-w-xl">
                <Title words={pageTitle} description={pageDescription} />
                <p className="text-slate-600 text-sm">{copy.loadingStudy}</p>
            </section>
        );
    }

    return (
        <section className="space-y-8 mx-auto w-full min-w-0 max-w-xl">
            <Title words={pageTitle} description={pageDescription} />
            <StudyForm study={existingStudy} values={values} onChange={setValues} isAdmin={isAdmin} userId={auth.id} />
            <FooterButtons
                func1={handleSave}
                words1={isNew ? copy.saveStudy : copy.saveChanges}
                primaryAction="save"
                type1="button"
                disabled1={saveMutation.isPending}
                isLoading1={saveMutation.isPending}
            />
        </section>
    );
}

export const Route = createFileRoute("/_app/studies/$studyId/edit")({
    component: StudyEditorPage,
});
